import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { APIList } from 'src/interfaces'
import { Entities } from 'src/enums'
import { DataLogger } from 'src/api'
import { useApiInstance } from './useApiInstance'
import {
  buildRequestParams,
  ObjectType,
  transformPage,
  transformPerPage,
} from './helpers'

interface AdminDataLoggerListGetParams {
  limit: number;
  offset: number;
  sort: string;
  search: string;
  contractorId?: number;
  projectId?: number;
  projectSectionId?: number;
}

interface RequestParams {
  params?: Partial<AdminDataLoggerListGetParams>;
  paramsTransform?: Array<(params: Record<string, any>) => Record<string, any>>;
  options?: Omit<
    UseQueryOptions<unknown, Error, APIList<DataLogger>>,
    'queryKey' | 'queryFn'
  >;
}

const schema: ObjectType['schema'] = {
  limit: 'number',
  offset: 'number',
  sort: 'string',
  search: 'string',
  contractorId: 'number',
  projectId: 'number',
  projectSectionId: 'number',
}

export const useDataLoggerListRequest = ({
  params = {},
  paramsTransform = [],
  options,
}: RequestParams = {}) => {
  const api = useApiInstance()

  const transformedParams = [
    transformPerPage,
    transformPage,
    ...paramsTransform,
  ].reduce((param, transform) => transform(param), params)

  const queryParams = buildRequestParams<AdminDataLoggerListGetParams>(
    transformedParams,
    schema,
  )

  return useQuery<unknown, Error, APIList<DataLogger>>(
    [Entities.Alerts, JSON.stringify(transformedParams)],
    async () => {
      const { limit, offset, sort, search, contractorId, projectId, projectSectionId } = queryParams
      const { data } = await api.v1AdminDataLoggersGet(
        limit,
        offset,
        sort,
        search,
        contractorId,
        projectId,
        projectSectionId,
      )
      return data
    },
    options,
  )
}
